import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { Layer, useAppStore, emptyLayerKeyframes } from '../../store/useAppStore';
import {
  ALL_PROPS, KeyframeProp, LayerKeyframes, LayerEasings, FRAME_OFFSET,
  ROW_H, SUB_H, PROP_COLOR, PROP_LABEL,
  kfKey, summaryColor, kfEasingShape, getAllFrames,
  SelectedLayerKf,
} from './timelineConstants';
import { LayerBarDragState } from '../../hooks/useLayerBarDrag';

// Stable empty-kfs reference — prevents LayerTrackRow memo from breaking when
// a layer has no keyframes and the store object reference changes.
const _emptyKfs = emptyLayerKeyframes();

// ── LayerLabelRow ─────────────────────────────────────────────────────────────
export interface LayerLabelRowProps {
  layer: Layer;
  isActive: boolean;
  isExpanded: boolean;
  displayFrame: number;
  /** Nesting depth for visual indentation (0 = root). */
  indentLevel?: number;
  onToggleExpanded: (id: string) => void;
  onAddAllKfs: (layerId: string, frame: number) => void;
  onRemoveAllKfs: (layerId: string, frame: number) => void;
  onAddPropKf: (layerId: string, prop: KeyframeProp, frame: number) => void;
  onRemoveKf: (layerId: string, prop: KeyframeProp, frame: number) => void;
}

export const LayerLabelRow = React.memo(({
  layer, isActive, isExpanded, displayFrame, indentLevel = 0,
  onToggleExpanded, onAddAllKfs, onRemoveAllKfs, onAddPropKf, onRemoveKf,
}: LayerLabelRowProps) => {
  const kfs = useAppStore((s) => s.layerKeyframes[layer.id]);
  const hasKfNow = kfs ? ALL_PROPS.some((p) => displayFrame in kfs[p]) : false;
  return (
  <div style={{ borderBottom: '1px solid #1e1e2e', borderLeft: indentLevel > 0 ? '2px solid rgba(245,166,35,0.35)' : undefined }}>
    <div style={{ height: ROW_H, display: 'flex', alignItems: 'center', gap: 4, padding: `0 5px 0 ${5 + indentLevel * 10}px`, background: isActive ? 'rgba(88,166,255,0.06)' : undefined }}>
      <span style={{ cursor: 'pointer', fontSize: 10, color: '#444', flexShrink: 0, width: 12, display: 'flex', alignItems: 'center' }} onClick={() => onToggleExpanded(layer.id)}>
        {isExpanded ? <DownOutlined /> : <RightOutlined />}
      </span>
      <span style={{ flex: 1, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isActive ? '#58a6ff' : '#aaa' }}>
        {layer.name}
      </span>
      <Tooltip title={hasKfNow ? 'Remove keyframe' : 'Add keyframe'}>
        <span style={{ cursor: 'pointer', flexShrink: 0, fontSize: 12, color: hasKfNow ? '#f5a623' : '#333', lineHeight: 1, padding: '0 2px' }}
          onClick={() => hasKfNow ? onRemoveAllKfs(layer.id, displayFrame) : onAddAllKfs(layer.id, displayFrame)}>
          ◆
        </span>
      </Tooltip>
    </div>
    {isExpanded && ALL_PROPS.map((prop) => {
      const propHasKf = kfs && (displayFrame in kfs[prop]);
      return (
        <div key={prop} style={{ height: SUB_H, display: 'flex', alignItems: 'center', padding: '0 5px 0 18px', gap: 4, background: '#0a0a16' }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: PROP_COLOR[prop], flexShrink: 0 }} />
          <span style={{ fontSize: 10, color: '#555', flex: 1 }}>{PROP_LABEL[prop]}</span>
          <Tooltip title={propHasKf ? `Remove ${PROP_LABEL[prop]} keyframe` : `Add ${PROP_LABEL[prop]} keyframe`}>
            <span style={{ cursor: 'pointer', fontSize: 10, color: propHasKf ? PROP_COLOR[prop] : '#2a2a3a', lineHeight: 1 }}
              onClick={() => propHasKf ? onRemoveKf(layer.id, prop, displayFrame) : onAddPropKf(layer.id, prop, displayFrame)}>
              ◆
            </span>
          </Tooltip>
        </div>
      );
    })}
  </div>
  );
});

// ── LayerTrackRow ─────────────────────────────────────────────────────────────
export interface LayerTrackRowProps {
  layer: Layer;
  isActive: boolean;
  isExpanded: boolean;
  selectedLayerIds: string[];
  pxPerFrame: number;
  scrollLeft: number;
  trackAreaW: number;
  totalFrames: number;
  barDragState: LayerBarDragState | null;
  dragState: { layerId: string; prop: KeyframeProp | 'summary'; fromFrame: number; toFrame: number; isMulti: boolean } | null;
  selectedKeySet: Set<string>;
  onBarPointerDown: (e: React.PointerEvent, layerId: string, mode: 'move' | 'left' | 'right', frameStart: number, frameEnd: number, selectedLayerIds?: string[]) => void;
  onBarPointerMove: (e: React.PointerEvent) => void;
  onBarPointerUp:   (e: React.PointerEvent, layerId: string, mode: 'move' | 'left' | 'right') => void;
  onSelectLayer: (e: React.MouseEvent) => void;
  onKfPointerDown: (e: React.PointerEvent, layerId: string, prop: KeyframeProp | 'summary', frame: number) => void;
  onKfPointerMove: (e: React.PointerEvent) => void;
  onKfPointerUp: (e: React.PointerEvent, layerId: string, prop: KeyframeProp | 'summary', fromFrame: number) => void;
  onSetCurrentFrame: (frame: number) => void;
  onAddAllKfs: (layerId: string, frame: number) => void;
  onAddPropKf: (layerId: string, prop: KeyframeProp, frame: number) => void;
  onRemoveAllKfsAndDeselect: (layerId: string, frame: number) => void;
  onRemovePropKfWithSnapshot: (layerId: string, prop: KeyframeProp, frame: number) => void;
  onOpenEasingMenu: (menu: { x: number; y: number; layerId: string; prop: KeyframeProp | 'summary'; frame: number }) => void;
  onSummaryKfClick: (layerId: string, frame: number, kfs: LayerKeyframes) => void;
  onPropKfClick: (layerId: string, prop: KeyframeProp, frame: number) => void;
  frameFromTrack: (e: { clientX: number }) => number;
  kfDragMovedRef: React.MutableRefObject<boolean>;
}

export const LayerTrackRow = React.memo(({
  layer, isActive, isExpanded, selectedLayerIds,
  pxPerFrame, scrollLeft, trackAreaW, totalFrames,
  barDragState, dragState, selectedKeySet,
  onBarPointerDown, onBarPointerMove, onBarPointerUp,
  onSelectLayer,
  onKfPointerDown, onKfPointerMove, onKfPointerUp,
  onSetCurrentFrame, onAddAllKfs, onAddPropKf,
  onRemoveAllKfsAndDeselect, onRemovePropKfWithSnapshot,
  onOpenEasingMenu, onSummaryKfClick, onPropKfClick,
  frameFromTrack, kfDragMovedRef,
}: LayerTrackRowProps) => {
  const kfs        = useAppStore((s) => s.layerKeyframes[layer.id] ?? _emptyKfs);
  const layerEasings = useAppStore((s) => s.keyframeEasings[layer.id]);
  const allFr      = useMemo(() => getAllFrames(kfs), [kfs]);
  // ── Layer timing bar geometry ──────────────────────────────────────────────
  const HANDLE_W  = 6;
  const origStart = layer.frameStart ?? 0;
  const origEnd   = layer.frameEnd   ?? (totalFrames - 1);
  let visStart = origStart;
  let visEnd   = origEnd;
  if (barDragState && barDragState.mode === 'move' && barDragState.draggedLayerIds.includes(layer.id)) {
    // For move mode, apply delta to all dragged layers
    const d = barDragState.currentDelta;
    visStart = origStart + d;
    visEnd = origEnd + d;
    
    // Apply same boundary clamping as in useLayerBarDrag so preview matches actual result
    if (visStart < 0) {
      visStart = 0;
    } else if (visStart > totalFrames - 1) {
      visStart = totalFrames - 1;
    }
    
    if (visEnd < 0) {
      visEnd = 0;
    } else if (visEnd > totalFrames - 1) {
      visEnd = totalFrames - 1;
    }
    
    // Ensure bar is at least 1 frame long
    if (visEnd <= visStart) {
      visEnd = visStart + 1;
      if (visEnd > totalFrames - 1) {
        visEnd = totalFrames - 1;
        visStart = visEnd - 1;
      }
    }
  } else if (barDragState && barDragState.layerId === layer.id) {
    // For left/right trim, only apply to primary layer
    const d = barDragState.currentDelta;
    if (barDragState.mode === 'left')  visStart = Math.min(origStart + d, origEnd - 1);
    else if (barDragState.mode === 'right') visEnd   = Math.max(origEnd + d, origStart + 1);
  }
  const barLeft  = visStart * pxPerFrame;
  const barWidth = (visEnd - visStart + 1) * pxPerFrame;
  const hidden = !layer.visible;
  // When the layer eye is off, use a diagonal stripe pattern over a muted base.
  const stripeColor  = isActive ? 'rgba(88,166,255,0.18)' : 'rgba(100,100,130,0.14)';
  const stripeGap    = 'transparent';
  const barBackground = hidden
    ? `repeating-linear-gradient(135deg, ${stripeColor} 0px, ${stripeColor} 2px, ${stripeGap} 2px, ${stripeGap} 7px)`
    : isActive ? 'rgba(88,166,255,0.15)' : 'rgba(100,100,130,0.10)';
  const barColor    = barBackground;
  const borderColor = hidden
    ? (isActive ? 'rgba(88,166,255,0.25)' : 'rgba(100,100,130,0.18)')
    : (isActive ? 'rgba(88,166,255,0.4)'  : 'rgba(100,100,130,0.25)');
  const handleColor = hidden
    ? (isActive ? 'rgba(88,166,255,0.25)' : 'rgba(100,100,130,0.18)')
    : (isActive ? 'rgba(88,166,255,0.55)' : 'rgba(100,100,130,0.32)');
  const kfOpacity = hidden ? 0.35 : 1;
  // When the bar is being moved, offset kf rendering by the same drag delta so kfs track with the bar.
  const barMoveDelta = (barDragState?.draggedLayerIds.includes(layer.id) && barDragState.mode === 'move') ? barDragState.currentDelta : 0;

  return (
  <div style={{ borderBottom: '1px solid #1e1e2e', position: 'relative' }}>
    {/* ── Summary track row ── */}
    <div
      style={{ height: ROW_H, position: 'relative', cursor: 'crosshair', background: isActive ? 'rgba(88,166,255,0.03)' : hidden ? 'rgba(0,0,0,0)' : '#0e0e1a' }}
      onClick={(e) => { if (!(e.target as HTMLElement).dataset.kf) { onSelectLayer(e); onSetCurrentFrame(frameFromTrack(e)); } }}
      onDoubleClick={(e) => { e.preventDefault(); const f = frameFromTrack(e); onSelectLayer(e); onSetCurrentFrame(f); onAddAllKfs(layer.id, f); }}
    >
      {/* ── Layer timing bar ── */}
      <div style={{
        position: 'absolute', left: barLeft, width: barWidth, top: 2, bottom: 2,
        background: barColor, border: `1px solid ${borderColor}`, borderRadius: 2,
        zIndex: 1, cursor: 'grab', boxSizing: 'border-box',
      }}
        onPointerDown={(e) => onBarPointerDown(e, layer.id, 'move', origStart, origEnd, selectedLayerIds)}
        onPointerMove={onBarPointerMove}
        onPointerUp={(e) => onBarPointerUp(e, layer.id, 'move')}
        onClick={(e) => { e.stopPropagation(); onSelectLayer(e); }}
      >
        {/* Left resize handle */}
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: HANDLE_W,
          background: handleColor, cursor: 'ew-resize', borderRadius: '1px 0 0 1px',
        }}
          onPointerDown={(e) => { e.stopPropagation(); onBarPointerDown(e, layer.id, 'left', origStart, origEnd, selectedLayerIds); }}
          onPointerMove={onBarPointerMove}
          onPointerUp={(e) => onBarPointerUp(e, layer.id, 'left')}
        />
        {/* Right resize handle */}
        <div style={{
          position: 'absolute', right: 0, top: 0, bottom: 0, width: HANDLE_W,
          background: handleColor, cursor: 'ew-resize', borderRadius: '0 1px 1px 0',
        }}
          onPointerDown={(e) => { e.stopPropagation(); onBarPointerDown(e, layer.id, 'right', origStart, origEnd, selectedLayerIds); }}
          onPointerMove={onBarPointerMove}
          onPointerUp={(e) => onBarPointerUp(e, layer.id, 'right')}
        />
      </div>
      {barDragState && barDragState.draggedLayerIds.includes(layer.id) && barDragState.currentDelta !== 0 && (<>
        {(barDragState.mode === 'left' || barDragState.mode === 'move') && (() => {
          const flip = (scrollLeft + trackAreaW - barLeft) < 32;
          return (
            <div style={{ position: 'absolute', left: barLeft + (flip ? -4 : 4), top: '50%', transform: `${flip ? 'translateX(-100%)' : ''} translateY(-50%)`, pointerEvents: 'none', zIndex: 20, background: 'rgba(10,10,20,0.9)', color: '#f5a623', fontSize: 10, borderRadius: 3, padding: '1px 4px', whiteSpace: 'nowrap', border: '1px solid rgba(245,166,35,0.35)', lineHeight: '14px' }}>
              {visStart + FRAME_OFFSET}
            </div>
          );
        })()}
        {(barDragState.mode === 'right' || barDragState.mode === 'move') && (() => {
          const edgePx = barLeft + barWidth;
          const flip = (scrollLeft + trackAreaW - edgePx) < 32;
          return (
            <div style={{ position: 'absolute', left: edgePx + (flip ? -4 : 4), top: '50%', transform: `${flip ? 'translateX(-100%)' : ''} translateY(-50%)`, pointerEvents: 'none', zIndex: 20, background: 'rgba(10,10,20,0.9)', color: '#f5a623', fontSize: 10, borderRadius: 3, padding: '1px 4px', whiteSpace: 'nowrap', border: '1px solid rgba(245,166,35,0.35)', lineHeight: '14px' }}>
              {visEnd + FRAME_OFFSET}
            </div>
          );
        })()}
      </>)}
      {allFr.map((frame) => {
        const x = (frame + barMoveDelta) * pxPerFrame;
        if (x < scrollLeft - 10 || x > scrollLeft + trackAreaW + 10) return null;
        const color = summaryColor(kfs, frame);
        const isBeingDragged = dragState
          ? dragState.isMulti
            ? ALL_PROPS.some((p) => selectedKeySet.has(kfKey(layer.id, p, frame)))
            : dragState.layerId === layer.id && dragState.prop === 'summary' && dragState.fromFrame === frame
          : false;
        const isSelected = ALL_PROPS.some((p) => selectedKeySet.has(kfKey(layer.id, p, frame)));
        const delta = dragState ? dragState.toFrame - dragState.fromFrame : 0;
        const ghostX = isBeingDragged && delta !== 0 ? (frame + delta) * pxPerFrame : null;
        return (
          <React.Fragment key={frame}>
            <div data-kf="1"
              style={{ position: 'absolute', left: x, top: 1, bottom: 1, width: 2, background: color, transform: 'translateX(-1px)', cursor: isBeingDragged ? 'grabbing' : 'grab', zIndex: 3, borderRadius: 1, boxShadow: isSelected ? `0 0 0 1px #fff, 0 0 6px #58a6ff` : `0 0 3px ${color}88`, opacity: isBeingDragged ? 0.3 : kfOpacity }}
              title={`f${frame + FRAME_OFFSET} — drag to move, right-click: easing, dbl-click: remove`}
              onPointerDown={(e) => onKfPointerDown(e, layer.id, 'summary', frame)}
              onPointerMove={onKfPointerMove}
              onPointerUp={(e) => onKfPointerUp(e, layer.id, 'summary', frame)}
              onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onOpenEasingMenu({ x: e.clientX, y: e.clientY, layerId: layer.id, prop: 'summary', frame }); }}
              onClick={(e) => {
                e.stopPropagation();
                if (!kfDragMovedRef.current) onSummaryKfClick(layer.id, frame, kfs);
                kfDragMovedRef.current = false;
              }}
              onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemoveAllKfsAndDeselect(layer.id, frame); }}
            />
            {ghostX !== null && (<>
              <div style={{ position: 'absolute', left: ghostX, top: 1, bottom: 1, width: 2, background: color, transform: 'translateX(-1px)', zIndex: 4, borderRadius: 1, boxShadow: `0 0 6px ${color}`, opacity: 0.9, outline: '1px solid white', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', left: ghostX, top: '50%', transform: 'translateX(-50%) translateY(-50%)', pointerEvents: 'none', zIndex: 20, background: 'rgba(10,10,20,0.9)', color: '#f5a623', fontSize: 10, borderRadius: 3, padding: '1px 4px', whiteSpace: 'nowrap', border: '1px solid rgba(245,166,35,0.35)', lineHeight: '14px' }}>
                {frame + delta + FRAME_OFFSET}
              </div>
            </>)}
          </React.Fragment>
        );
      })}
    </div>
    {/* ── Per-property sub-rows ── */}
    {isExpanded && ALL_PROPS.map((prop) => {
      const propFrames = kfs ? Object.keys(kfs[prop]).map(Number) : [];
      return (
        <div key={prop}
          style={{ height: SUB_H, position: 'relative', cursor: 'crosshair', background: '#090914', borderTop: `1px solid #1a1a28` }}
          onClick={(e) => { if (!(e.target as HTMLElement).dataset.kf) onSetCurrentFrame(frameFromTrack(e)); }}
          onDoubleClick={(e) => { e.preventDefault(); const f = frameFromTrack(e); onSetCurrentFrame(f); onAddPropKf(layer.id, prop, f); }}
        >
          <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1, background: `${PROP_COLOR[prop]}1a`, pointerEvents: 'none' }} />
          {propFrames.map((frame) => {
            const x = (frame + barMoveDelta) * pxPerFrame;
            if (x < scrollLeft - 8 || x > scrollLeft + trackAreaW + 8) return null;
            const isBeingDragged = dragState
              ? dragState.isMulti
                ? selectedKeySet.has(kfKey(layer.id, prop, frame))
                : dragState.layerId === layer.id && dragState.prop === prop && dragState.fromFrame === frame
              : false;
            const isSelected = selectedKeySet.has(kfKey(layer.id, prop, frame));
            const delta = dragState ? dragState.toFrame - dragState.fromFrame : 0;
            const ghostX = isBeingDragged && delta !== 0 ? (frame + delta) * pxPerFrame : null;
            return (
              <React.Fragment key={frame}>
                <div data-kf="1"
                  style={{ position: 'absolute', left: x, top: '50%', width: 6, height: 6, background: PROP_COLOR[prop], borderRadius: kfEasingShape(layerEasings, prop, frame).borderRadius, transform: `translateX(-3px) translateY(-3px) rotate(${kfEasingShape(layerEasings, prop, frame).rotate})`, cursor: isBeingDragged ? 'grabbing' : 'grab', zIndex: 3, boxShadow: isSelected ? '0 0 0 1.5px #ffffff, 0 0 6px #58a6ff' : `0 0 3px ${PROP_COLOR[prop]}88`, opacity: isBeingDragged ? 0.3 : kfOpacity }}
                  title={`${PROP_LABEL[prop]} f${frame + FRAME_OFFSET} — drag to move, right-click: easing, dbl-click: remove`}
                  onPointerDown={(e) => onKfPointerDown(e, layer.id, prop, frame)}
                  onPointerMove={onKfPointerMove}
                  onPointerUp={(e) => onKfPointerUp(e, layer.id, prop, frame)}
                  onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onOpenEasingMenu({ x: e.clientX, y: e.clientY, layerId: layer.id, prop, frame }); }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!kfDragMovedRef.current) onPropKfClick(layer.id, prop, frame);
                    kfDragMovedRef.current = false;
                  }}
                  onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemovePropKfWithSnapshot(layer.id, prop, frame); }}
                />
                {ghostX !== null && (<>
                  <div style={{ position: 'absolute', left: ghostX, top: '50%', width: 6, height: 6, background: PROP_COLOR[prop], transform: 'translateX(-3px) translateY(-3px) rotate(45deg)', zIndex: 4, boxShadow: `0 0 6px ${PROP_COLOR[prop]}`, opacity: 0.9, outline: '1.5px solid white', pointerEvents: 'none' }} />
                </>)}
              </React.Fragment>
            );
          })}
        </div>
      );
    })}
  </div>
  );
});
export type { SelectedLayerKf };
